import React, { useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";

// @YUp
import * as Yup from "yup";
import { Button, Grid, TextField, MenuItem, Select, FormControl, Box, Chip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Notification } from "../../utils/Notification";
import CustomSelect from "../../uicomponents/CustomSelect";
import TextArea from "../../uicomponents/TextArea";
import ReactCustomSelect from "../../uicomponents/ReactCustomSelect";
import { useGetJobCodes, useGetJobSkills, useUpdateCandidate, useAutofillFromResume } from "../../hooks/useCandidateManagement";
import useAuth from "../../hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";

const EditCandidate = () => {
  const { user: userDetails } = useAuth();
  const formikRef = React.useRef(null);

  const navigate = useNavigate();

  const fileInputRefs = {
    Resume: useRef(null),
    PAN: useRef(null),
    Aadhar: useRef(null),
  };

  const location = useLocation();
  const candidateDetails = location.state?.CandidateDetails;
  
  const getCleanName = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    let name = raw.split('?')[0];
    if (name.includes('/') || name.includes('\\')) {
      try {
        name = decodeURIComponent(name.split('/').pop().split('\\').pop());
      } catch {
        name = name.split('/').pop().split('\\').pop();
      }
    }
    if (candidateDetails?.job_code && name.startsWith(candidateDetails.job_code + '_')) {
      name = name.substring(candidateDetails.job_code.length + 1);
    }
    while (/^[A-Za-z0-9]*[0-9-][A-Za-z0-9]*_/.test(name)) {
      name = name.replace(/^[A-Za-z0-9]*[0-9-][A-Za-z0-9]*_/, '');
    }
    return name;
  };

  let initialAadharFile = getCleanName(candidateDetails?.s3Key_aadhar_doc || (candidateDetails?.aadhar_doc && (candidateDetails.aadhar_doc.includes('.') || candidateDetails.aadhar_doc.includes('/')) ? candidateDetails.aadhar_doc : null));
  let initialPanFile = getCleanName(candidateDetails?.s3Key_pan_doc || (candidateDetails?.pan_doc && (candidateDetails.pan_doc.includes('.') || candidateDetails.pan_doc.includes('/')) ? candidateDetails.pan_doc : null));
  let initialResumeFile = getCleanName(candidateDetails?.profile_path || candidateDetails?.s3Key_profile_path || null);

  const [files, setFiles] = React.useState({
    Resume: initialResumeFile,
    PAN: initialPanFile,
    Aadhar: initialAadharFile,
  });
  const [isFormSubmitted, setIsFormSubmitted] = React.useState(false);

  // Snackbar
  const [alert, setAlert] = React.useState(false);
  const [alertContent, setAlertContent] = React.useState("");
  const [alertType, setAlertType] = React.useState("");

  const handleAlert = () => {
    setAlert(false);
  };

  const handleFileChange = (event, docType) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const isIdentityDoc = docType === "PAN" || docType === "Aadhar";
      const allowedMimeTypes = [
        'application/msword', // .doc
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'application/pdf' // .pdf
      ];
      const allowedExtensions = ['.doc', '.docx', '.pdf'];

      if (isIdentityDoc) {
        allowedMimeTypes.push('image/jpeg', 'image/png', 'image/jpg');
        allowedExtensions.push('.jpeg', '.jpg', '.png');
      }

      // Check MIME type (primary validation)
      const isValidMime = allowedMimeTypes.includes(selectedFile.type);

      // Fallback: Check file extension
      const fileExtension = '.' + selectedFile.name.split('.').pop().toLowerCase();
      const isValidExtension = allowedExtensions.includes(fileExtension);

      if (isValidMime || isValidExtension) {
        // Optional: File size validation (max 5MB)
        if (selectedFile.size > 5 * 1024 * 1024) {
          setAlertContent("File too large. Please select a document under 5MB.");
          setAlertType("error");
          setAlert(true);
          event.target.value = ''; // Clear input
          return;
        }

        setFiles((prevFiles) => ({
          ...prevFiles,
          [docType]: selectedFile,
        }));
      } else {
        // Display error for invalid file type
        setAlertContent(`Invalid file type. ${isIdentityDoc ? 'Please select a Word, PDF, or Image file.' : 'Please select a Word document (.doc or .docx) or a PDF (.pdf).'}`);
        setAlertType("error");
        setAlert(true);
        event.target.value = ''; // Clear invalid file
      }
    }
  };

  const handleAutofill = (docType) => {
    const selectedFile = files[docType];
    if (!selectedFile) {
      setAlertContent("Please select a resume file first.");
      setAlertType("warning");
      setAlert(true);
      return;
    }

    // Trigger Autofill
    autofillResume(selectedFile).then((data) => {
      if (data && formikRef.current) {
        const currentValues = formikRef.current.values;

        const hasData = data.candidate_name || data.email || data.mobile || data.candidate_current_location;

        if (!hasData) {
          setAlertContent("Could not extract details from this resume. Please fill manually.");
          setAlertType("warning");
          setAlert(true);
          return;
        }

        if (data.candidate_name && !currentValues.candidate_name) formikRef.current.setFieldValue('candidate_name', data.candidate_name);
        if (data.email && !currentValues.email) formikRef.current.setFieldValue('email', data.email);
        if (data.mobile && !currentValues.mobile) formikRef.current.setFieldValue('mobile', data.mobile);
        if (data.candidate_current_location && !currentValues.candidate_current_location) formikRef.current.setFieldValue('candidate_current_location', data.candidate_current_location);

        setAlertContent("Resume data autofilled successfully.");
        setAlertType("success");
        setAlert(true);
      }
    }).catch((err) => {
      console.error("Autofill failed", err);
      setAlertContent("Failed to extract data from resume.");
      setAlertType("warning");
      setAlert(true);
    });
  };

  const handleRemoveFile = (docType) => {
    setFiles((prevFiles) => ({
      ...prevFiles,
      [docType]: null,
    }));
  };

  const [selectedTechSkills, setSelectedTechSkills] = React.useState(
    Array.isArray(candidateDetails?.techSkills) ? candidateDetails.techSkills : []
  );

  const isDocFile = (filename) => {
    if (!filename || typeof filename !== 'string') return false;
    const lower = filename.toLowerCase();
    return lower.endsWith('.pdf') || lower.endsWith('.doc') || lower.endsWith('.docx') ||
           lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.png');
  };

  const initialValues = {
    job_code: candidateDetails.job_code || null,
    candidate_name: candidateDetails.candidate_name,
    candidate_current_location: candidateDetails.candidate_current_location,
    mobile: candidateDetails.mobile,
    email: candidateDetails.email,
    aadhar_doc: (candidateDetails.aadhar_doc && !isDocFile(candidateDetails.aadhar_doc)) ? candidateDetails.aadhar_doc : '',
    currency: candidateDetails.currency,
    budget: candidateDetails.budget,
    budget_eom: candidateDetails.budget_eom,
    important_note: candidateDetails.important_note,
    coreSkillId: candidateDetails.coreSkillId || '',
    techSkills: [],
  };

  const { supplierId } = useAuth();

  // React Query Hooks
  const { data: jobCodeList } = useGetJobCodes(supplierId);
  const [selectedJobCode, setSelectedJobCode] = React.useState(candidateDetails.job_code || '');

  const { data: jobSkills } = useGetJobSkills(selectedJobCode);
  const isNonResume = jobSkills 
    ? (jobSkills.pipeline_type === 'non_resume_based')
    : (candidateDetails?.pipeline_type === 'non_resume_based');

  const { mutate: updateCandidateProfile } = useUpdateCandidate();
  const { mutateAsync: autofillResume } = useAutofillFromResume();
  const queryClient = useQueryClient();

  const isFormClean = (values) => {
    if (values.job_code !== (candidateDetails.job_code || null)) return false;
    if (values.candidate_name !== candidateDetails.candidate_name) return false;
    if (values.candidate_current_location !== candidateDetails.candidate_current_location) return false;
    if (values.mobile !== candidateDetails.mobile) return false;
    if (values.email !== candidateDetails.email) return false;
    if (values.currency !== candidateDetails.currency) return false;
    if (values.budget !== candidateDetails.budget) return false;
    if (values.budget_eom !== candidateDetails.budget_eom) return false;
    if (values.important_note !== candidateDetails.important_note) return false;
    if (files.Resume !== candidateDetails.profile_path) return false;
    if (files.PAN !== candidateDetails.pan_doc) return false;
    if (files.Aadhar !== initialAadharFile) return false;

    const origTech = Array.isArray(candidateDetails?.techSkills)
      ? candidateDetails.techSkills
      : typeof candidateDetails?.techSkills === "string"
        ? candidateDetails.techSkills.split(",").map((s) => s.trim()).filter(Boolean)
        : [];

    const currTech = Array.isArray(values.techSkills) ? values.techSkills : [];
    if (origTech.length !== currTech.length || !origTech.every(val => currTech.includes(val))) {
      return false;
    }

    return true;
  };

  const onSubmit = async (values) => {
    if (!isNonResume && files.Resume == null) {
      setIsFormSubmitted(true);
    } else {
      const formData = new FormData();
      formData.append("job_code", values.job_code);
      formData.append("candidate_name", values.candidate_name);
      formData.append(
        "candidate_current_location",
        values.candidate_current_location
      );
      formData.append("mobile", values.mobile);
      formData.append("email", values.email);
      formData.append("aadhar_doc", values.aadhar_doc || '');
      formData.append("currency", values.currency);
      formData.append("budget", values.budget);
      formData.append("budget_eom", values.budget_eom);
      formData.append("important_note", values.important_note);
      formData.append("coreSkillId", values?.coreSkillId);
      const techSkillsArray = Array.isArray(values.techSkills)
        ? values.techSkills
        : String(values.techSkills || '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);
      formData.append("techSkills", JSON.stringify(techSkillsArray));
      formData.append('suppliercode', userDetails?.supplierId);
      
      if (files.Resume && files.Resume !== candidateDetails.profile_path) {
        formData.append("file", files.Resume);
      }

      if (files.PAN instanceof File) {
        formData.append("pan_doc", files.PAN);
      } else if (!files.PAN && candidateDetails.pan_doc) {
        formData.append("remove_pan_doc", "true");
      }
      
      if (files.Aadhar instanceof File) {
        formData.append("aadhar_file", files.Aadhar);
      } else if (!files.Aadhar && initialAadharFile) {
        formData.append("remove_aadhar_doc", "true");
      }

      updateCandidateProfile({
        profileId: candidateDetails.profile_id,
        formData: formData
      }, {
        onSuccess: async (res) => {
          setIsFormSubmitted(false);
          setAlertContent(res.message || "Candidate updated successfully");
          setAlertType("success");
          setAlert(true);
          // Wait for the candidates list to refetch fresh data before navigating,
          // so the Edit page won't show stale pan_doc / s3Key_aadhar_doc values.
          await queryClient.refetchQueries({ queryKey: ['candidates'] });
          navigate('/candidate_data');
        },
        onError: (error) => {
          setIsFormSubmitted(false);
          setAlertContent(error.response?.data?.error || error.message);
          setAlertType("error");
          setAlert(true);
        }
      });
    }
  };

  const emailRegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const validationSchema = Yup.object().shape({
    job_code: Yup.string().required("This field is required."),
    candidate_name: Yup.string()
      .min(2, "Minimum 2 characters are required.")
      .max(50, "Maximum length is 50 characters.")
      .matches(/^[A-Za-z\s]+$/, "Only alphabetic characters and spaces are allowed.")
      .matches(/^[^\s].*$/, "Name should not start with a space.")
      .required("This field is required."),
    candidate_current_location: Yup.string()
      .required("This field is required.")
      .min(1, "Minimum 1 caharacter is required.")
      .max(50, "Maximum length is 200 characters.")
      .matches(/^[^\s].*$/, "Location should not start with a space."),
    mobile: Yup.string()
      .required("This field is required.")
      .matches(
        /^\+91[6-9]\d{9}$/,
        "Please enter a valid 13-digit number starting with +91 and followed by 10 digits starting with 6-9."
      ),
    email: Yup.string()
      .matches(emailRegExp, "Email must have @ symbol and domain name with a dot.")
      .required("This field is required."),
    aadhar_doc: Yup.string()
      .nullable()
      .test("aadhaar-format", "Aadhaar Number must be exactly 12 digits.", (val) => !val || /^\d{12}$/.test(val)),
    currency: Yup.string().required("This field is required."),
    budget: Yup.string().required("This field is required."),
    budget_eom: Yup.string().required("This field is required."),
  });

  const navigateToPreviousPage = () => {
    navigate(-1);
  };

  // Sync core skills
  React.useEffect(() => {
    if (jobSkills?.coreSkills) {
      if (formikRef.current) {
        formikRef.current.setFieldValue("coreSkillId", jobSkills.coreSkills.id || "");
      }
    }
  }, [jobSkills]);

  // Sync tech skills
  React.useEffect(() => {
    if (candidateDetails?.techSkills) {
      const candidateSkillsArray = Array.isArray(candidateDetails.techSkills)
        ? candidateDetails.techSkills
        : typeof candidateDetails.techSkills === "string"
          ? candidateDetails.techSkills.split(",").map((s) => s.trim())
          : [];

      let validSkills = candidateSkillsArray;

      if (jobSkills?.techSkills && selectedJobCode !== candidateDetails.job_code) {
        const jobTechSet = new Set(jobSkills.techSkills);
        validSkills = candidateSkillsArray.filter((skill) => jobTechSet.has(skill));
      }

      setSelectedTechSkills(validSkills);
      if (formikRef.current) {
        formikRef.current.setFieldValue("techSkills", validSkills);
      }
    }
  }, [jobSkills, candidateDetails?.techSkills, selectedJobCode, candidateDetails?.job_code]);

  return (
    <div style={{ marginTop: 20 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={navigateToPreviousPage}>
        Back
      </Button>
      <div
        style={{
          border: "1px solid #989898",
          borderRadius: "10px",
          padding: "15px",
        }}
      >
        <div
          style={{
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            fontWeight: "500",
            letterSpacing: 1,
            textAlign: "left",
          }}
        >
          <span
            style={{
              fontSize: "20px",
            }}
          >
            Edit Profile
          </span>
          <div style={{ display: "block" }}>
            <Formik
              innerRef={formikRef}
              initialValues={initialValues}
              onSubmit={onSubmit}
              validationSchema={validationSchema}
            >
              {(props) => {
                console.log("EditCandidate State - Errors:", props.errors, "isValid:", props.isValid, "isClean:", isFormClean(props.values));
                return (
                <Form noValidate>
                  <Grid container spacing={2}>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Job Code</span>
                      <ReactCustomSelect
                        name="job_code"
                        props={props}
                        options={jobCodeList?.result?.map((job) => ({
                          value: job.job_code,
                          label: job.title ? `${job.job_code} - ${job.title}` : job.job_code,
                        })) || []}
                        disabled={candidateDetails ? true : false}
                        onChange={(selectedOption) => {
                          setSelectedJobCode(selectedOption?.value);
                          console.log("Selected option:", selectedOption);
                          // jobSkills will update automatically via Query
                          setSelectedTechSkills([]);
                          if (formikRef.current) {
                            formikRef.current.setFieldValue('coreSkillId', '');
                            formikRef.current.setFieldValue('techSkills', []);
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Core Skills</span>
                      <Field
                        name="coreskill"
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            // label="Core Skills"
                            value={jobSkills?.coreSkills?.name || candidateDetails?.core_skill_name?.replace(/_/g, ' ') || ''}
                            InputProps={{
                              readOnly: true,
                              disabled: true  // Always disabled
                            }}
                            size="small"
                            error={props.errors.coreskill && props.touched.coreskill}
                            helperText={<ErrorMessage name="coreskill" />}
                          />
                        )}
                      />

                    </Grid>

                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: 16 }}>Technical Skills</span>
                      <FormControl fullWidth size="small">
                        <Select
                          multiple
                          value={selectedTechSkills}
                          onChange={(e) => {
                            const newTechSkills = e.target.value;
                            setSelectedTechSkills(newTechSkills);
                            if (formikRef.current) {
                              formikRef.current.setFieldValue('techSkills', newTechSkills);
                            }
                          }}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip key={value} label={value} size="small" />
                              ))}
                            </Box>
                          )}
                        >
                          {Array.from(new Set([
                            ...(jobSkills?.techSkills || []),
                            ...(Array.isArray(selectedTechSkills) ? selectedTechSkills : [])
                          ])).map((skill) => (
                            <MenuItem
                              key={skill}
                              value={skill}
                              style={
                                Array.isArray(selectedTechSkills) && selectedTechSkills.includes(skill)
                                  ? { backgroundColor: '#e3f2fd', fontWeight: 500 }
                                  : {}
                              }
                            >
                              {skill}
                            </MenuItem>
                          ))}
                        </Select>

                      </FormControl>
                    </Grid>

                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Candidate Name</span>
                      <Field
                        as={TextField}
                        name="candidate_name"
                        fullWidth
                        error={
                          props.errors.candidate_name &&
                          props.touched.candidate_name
                        }
                        size="small"
                        helperText={<ErrorMessage name="candidate_name" />}
                        required
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Location</span>
                      <Field
                        as={TextField}
                        name="candidate_current_location"
                        fullWidth
                        error={
                          props.errors.candidate_current_location &&
                          props.touched.candidate_current_location
                        }
                        size="small"
                        helperText={
                          <ErrorMessage name="candidate_current_location" />
                        }
                        required
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Mobile</span>
                      <Field
                        as={TextField}
                        name="mobile"
                        fullWidth
                        error={props.errors.mobile && props.touched.mobile}
                        size="small"
                        helperText={<ErrorMessage name="mobile" />}
                        required
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Email</span>
                      <Field
                        as={TextField}
                        name="email"
                        fullWidth
                        error={props.errors.email && props.touched.email}
                        size="small"
                        helperText={<ErrorMessage name="email" />}
                        required
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Aadhaar Number</span>
                      <Field
                        as={TextField}
                        name="aadhar_doc"
                        placeholder="Enter 12-digit Aadhaar Number"
                        fullWidth
                        error={props.errors.aadhar_doc && props.touched.aadhar_doc}
                        size="small"
                        helperText={<ErrorMessage name="aadhar_doc" />}
                        required
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Currency</span>
                      <CustomSelect
                        name="currency"
                        props={props}
                        options={allCurrency}
                      />
                    </Grid>

                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Budget</span>
                      <Field
                        as={TextField}
                        name="budget"
                        fullWidth
                        error={props.errors.budget && props.touched.budget}
                        size="small"
                        onInput={(e) =>
                        (e.target.value = e.target.value.replace(
                          /[^0-9.]/g,
                          ""
                        ))
                        }
                        helperText={<ErrorMessage name="budget" />}
                        required
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Budget UOM</span>
                      <CustomSelect
                        name="budget_eom"
                        props={props}
                        options={budgetUOM}
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}></Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Notes (Optional)</span>
                      <TextArea name="important_note" props={props} />
                    </Grid>

                    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div
                        style={{
                          marginTop: 20,
                          border: "1px solid #989898",
                          borderRadius: "10px",
                          padding: "15px",
                        }}
                      >
                        <Grid container>
                          {Object.entries(files).map(
                            ([docType, file], index) => {
                                if (isNonResume && docType === 'Resume') return null;
                                return (
                                <Grid container spacing={2} key={index} style={{ marginBottom: "10px" }}>
                                  {/* 1st Column */}
                                  <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
                                    Upload {docType === "Resume" ? "Profile" : docType + " Document"}
                                  </Grid>

                                  {/* 2nd Column */}
                                  <Grid
                                    item
                                    xl={6}
                                    lg={6}
                                    md={6}
                                    sm={6}
                                    xs={6}
                                    display="flex"
                                    alignItems="center"
                                  >
                                    {file && (
                                      <>
                                        {file.name ? (
                                          <div>{file.name}</div>
                                        ) : (
                                          <div>{file}</div>
                                        )}
                                        <DeleteIcon
                                          style={{
                                            cursor: "pointer",
                                            color: "red",
                                            marginLeft: "5px",
                                          }}
                                          onClick={() =>
                                            handleRemoveFile(docType)
                                          }
                                        />
                                      </>
                                    )}
                                  </Grid>

                                  {/* 3rd Column */}
                                  <Grid
                                    item
                                    xl={3}
                                    lg={3}
                                    md={3}
                                    sm={3}
                                    xs={3}
                                    display="flex"
                                    justifyContent="flex-end"
                                  >
                                    <Box display="flex" gap={1}>
                                      <Button
                                        onClick={() =>
                                          fileInputRefs[docType].current.click()
                                        }
                                      >
                                        Browse
                                      </Button>
                                      {docType === "Resume" && (
                                        <Button
                                          variant="contained"
                                          color="secondary"
                                          onClick={() => handleAutofill(docType)}
                                          disabled={!file}
                                          style={{ height: '36px' }}
                                        >
                                          Autofill
                                        </Button>
                                      )}
                                    </Box>
                                    <input
                                      type="file"
                                      hidden="true"
                                      accept={docType === "Resume" ? ".doc,.docx,.pdf" : ".doc,.docx,.pdf,.jpg,.jpeg,.png"}
                                      onChange={(e) =>
                                        handleFileChange(e, docType)
                                      }
                                      ref={fileInputRefs[docType]}
                                    />
                                  </Grid>
                                 </Grid>
                               );
                            }
                          )}
                        </Grid>
                      </div>
                    </Grid>

                    {!isNonResume && isFormSubmitted && files.Resume == null && (
                      <div
                        style={{
                          color: "#d32f2f",
                          marginLeft: "2rem",
                          fontWeight: 400,
                          fontSize: "0.75rem",
                        }}
                      >
                        This Field is required
                      </div>
                    )}

                    {/* Submit */}
                    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Button
                          variant="contained"
                          aria-haspopup="true"
                          style={{
                            background: "#2777AA",
                            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                            fontSize: "16px",
                            fontWeight: "500",
                            letterSpacing: 1,
                          }}
                          type="submit"
                        >
                          Submit
                        </Button>
                      </div>
                    </Grid>
                  </Grid>
                  <Notification
                    message={alertContent}
                    open={alert}
                    severity={alertType}
                    onClose={handleAlert}
                  />
                </Form>
              )}}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCandidate;

const allCurrency = [
  { id: 1, value: "INR", label: "INR" },
  { id: 2, value: "USD", label: "USD" },
  { id: 3, value: "GBP", label: "GBP" },
];

const budgetUOM = [
  { id: 1, value: "perHour", label: "Per Hour" },
  { id: 2, value: "perMonth", label: "Per Month" },
  { id: 3, value: "perDay", label: "Per Day" },
];


